import JSZip from "jszip"
import { saveAs } from "file-saver"
import { type BundledCalibrationData } from "luxedo-rpc/src/types/BundledTypes"
import { Toast } from "toaster"

export async function downloadCalImages(cal: BundledCalibrationData) {
	Toast.text("Downloading calibration images. This may take a moment...")

	const paths = cal.s3urls.images
	const zipName = `cal-images-${cal.id}`

	if (!paths?.length) return

	// small concurrency to avoid hammering the network
	const CONCURRENCY = 4
	const queue = paths.map((url, i) => ({ url, i }))
	const zip = new JSZip()

	// track duplicates so we don’t overwrite zip entries
	const seen: Record<string, number> = {}

	const next = async () => {
		const item = queue.shift()
		if (!item) return

		try {
			const res = await fetch(item.url, { credentials: "omit", mode: "cors" })
			if (!res.ok) throw new Error(String(res.status))
			const ab = await res.arrayBuffer()

			// name from URL or fallback to index with guessed ext
			let name = basename(item.url) || `file-${item.i}${extFromCT(res.headers.get("content-type"))}`
			if (seen[name] != null) {
				seen[name] += 1
				const dot = name.lastIndexOf(".")
				name =
					dot > -1
						? `${name.slice(0, dot)} (${seen[name]})${name.slice(dot)}`
						: `${name} (${seen[name]})`
			} else {
				seen[name] = 0
			}

			zip.file(name, ab)
		} catch (e) {
			console.warn("Failed:", item.url, e)
		} finally {
			await next()
		}
	}

	await Promise.all(Array.from({ length: Math.min(CONCURRENCY, paths.length) }, next))
	const blob = await zip.generateAsync({
		type: "blob",
		compression: "DEFLATE",
		compressionOptions: { level: 6 },
	})

	saveAs(blob, zipName)
}

function basename(url: string) {
	try {
		const u = new URL(url)
		const last = u.pathname.split("/").filter(Boolean).pop()
		return last ? decodeURIComponent(last) : ""
	} catch {
		// not a valid absolute URL; try naive split
		const last = url.split("/").filter(Boolean).pop()
		return last ? decodeURIComponent(last) : ""
	}
}

function extFromCT(ct: string | null) {
	if (!ct) return ""
	if (ct.includes("png")) return ".png"
	if (ct.includes("webp")) return ".webp"
	if (ct.includes("jpeg") || ct.includes("jpg")) return ".jpg"
	if (ct.includes("gif")) return ".gif"
	return ""
}
